import { useCallback, useMemo, useState } from "react";

import { createColumnHelper, RowModel } from "@tanstack/react-table";
import { DateTime } from "luxon";

import useHaulierAccounts from "../../data/useHaulierAccounts";
import useWasteTracking, { WASTE_TRACKING_TABLE_URL } from "../../data/useWasteTracking";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccount } from "../../models/account/IAccount";
import {
  IUpsertWasteReceiptDraft,
  IWasteReceiptRow,
  WasteReceiptCarrierType,
  WasteReceiptStatus,
  WasteReceiptView,
} from "../../models/wasteTracking/IWasteReceiptRow";
import formatDateTime from "../../utils/formatDateTime";
import { haulierOptions } from "../../utils/hauliers";
import openInNewTab from "../../utils/openInNewTab";
import { carrierTypeLabels, fromLocalDateTimeInput, rowToDraft, toLocalDateTimeInput } from "../../utils/wasteTracking";
import { AccountCreateEditModal } from "../accounts/AccountCreateEditModal";
import useNotification from "../notifications/useNotifications";
import ClickToCopy from "../shared/ClickToCopy";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import PagedTable from "../shared/Tables/PagedTable";
import Tooltip from "../shared/Tooltip";
import WasteReceiptBulkUpdateModal from "./WasteReceiptBulkUpdateModal";
import { WasteReceiptCheckboxCell, WasteReceiptSelectCell, WasteReceiptTextCell } from "./WasteReceiptEditableCell";
import { WasteReceiptHaulierCell } from "./WasteReceiptHaulierCell";
import WasteReceiptStatusPill from "./WasteReceiptStatusPill";
import WasteReceiptSubmitModal from "./WasteReceiptSubmitModal";

const colHelper = createColumnHelper<IWasteReceiptRow>();

const carrierTypeOptions = [
  { value: WasteReceiptCarrierType.OwnCarrier, label: carrierTypeLabels[WasteReceiptCarrierType.OwnCarrier] },
  { value: WasteReceiptCarrierType.HouseholdDropOff, label: carrierTypeLabels[WasteReceiptCarrierType.HouseholdDropOff] },
  { value: WasteReceiptCarrierType.ThirdPartyCarrier, label: carrierTypeLabels[WasteReceiptCarrierType.ThirdPartyCarrier] },
];

interface WasteTrackingTableProps {
  view: WasteReceiptView;
  additionalBody: Record<string, any>;
}

export default function WasteTrackingTable({ view, additionalBody }: WasteTrackingTableProps) {
  const { upsertDraft, exclude, restore, amend, getReadyIds } = useWasteTracking("WasteTrackingTable");
  const { saveHandlers } = useDefaultCRUDHandlers("Waste receipt");
  const { addNotification } = useNotification();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitMode, setSubmitMode] = useState<"selected" | "ready">("selected");
  // The API stamps a "Site settings incomplete" issue on every row when DEFRA:* is missing.
  const [siteSettingsIncomplete, setSiteSettingsIncomplete] = useState(false);

  // Hauliers are accounts flagged isHaulier. The account form saves them through the
  // staff-level haulier route, so anyone who can use this page can add one.
  const { haulierAccounts } = useHaulierAccounts("WasteTrackingTable");
  const haulierList = haulierAccounts.data?.data;
  const [addHaulierOpen, setAddHaulierOpen] = useState(false);
  const [haulierTargetRow, setHaulierTargetRow] = useState<IWasteReceiptRow | null>(null);

  const editable = view !== "excluded";

  const commit = useCallback(
    (row: IWasteReceiptRow, change: Partial<IUpsertWasteReceiptDraft>) => {
      const draft: IUpsertWasteReceiptDraft = { ...rowToDraft(row), ...change };
      upsertDraft.mutate({ vehicleId: row.vehicleId, draft }, { onError: saveHandlers.onError });
    },
    [upsertDraft, saveHandlers]
  );

  const hasIssue = (row: IWasteReceiptRow, needle: string) => row.issues?.some((i) => i.toLowerCase().includes(needle)) ?? false;

  const cols = useMemo(
    () => [
      TableCheckboxCol(false),
      colHelper.accessor("vrm", {
        id: "vrm",
        header: "Vehicle",
        cell: (info) => (
          <div className="min-w-[10rem]">
            <ClickToCopy text={info.row.original.vrm ?? ""} />
            <p className="text-xs text-gray-600">
              {[info.row.original.make, info.row.original.model].filter(Boolean).join(" ")}
            </p>
            {info.row.original.stockNumber && <p className="text-xs text-gray-500">Stock {info.row.original.stockNumber}</p>}
          </div>
        ),
      }),
      colHelper.accessor("collectedOn", {
        id: "collectedOn",
        header: "Collected",
        cell: (info) => (
          <div className="min-w-[8rem]">
            <p>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT, "Not set")}</p>
            <p className="text-xs text-gray-500">{info.row.original.isDropOff ? "Drop-off" : "Collection"}</p>
            {info.row.original.contactName && <p className="text-xs text-gray-500">{info.row.original.contactName}</p>}
          </div>
        ),
      }),
      colHelper.display({
        id: "readiness",
        header: "Status",
        cell: (info) => {
          const row = info.row.original;
          const details = view === "submitted" ? row.warnings : row.issues;
          return (
            <div className="flex flex-col items-start gap-1">
              {details?.length ? (
                <Tooltip
                  content={
                    <ul className="list-disc pl-4 text-left">
                      {details.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  }
                >
                  <WasteReceiptStatusPill row={row} />
                </Tooltip>
              ) : (
                <WasteReceiptStatusPill row={row} />
              )}
              {row.wasteTrackingId && (
                <span className="text-xs">
                  <ClickToCopy text={row.wasteTrackingId} />
                </span>
              )}
            </div>
          );
        },
      }),
      colHelper.accessor("dateTimeReceived", {
        header: "Date received",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            type="datetime-local"
            width="w-48"
            value={toLocalDateTimeInput(info.getValue())}
            disabled={!editable}
            invalid={hasIssue(info.row.original, "date received")}
            onCommit={(v) => commit(info.row.original, { dateTimeReceived: fromLocalDateTimeInput(v) })}
          />
        ),
      }),
      colHelper.accessor("weightKg", {
        header: "Weight (kg)",
        enableSorting: false,
        cell: (info) => (
          <div className="flex flex-col gap-1">
            <WasteReceiptTextCell
              type="number"
              step="1"
              width="w-24"
              value={info.getValue() === null ? "" : String(info.getValue())}
              disabled={!editable}
              invalid={hasIssue(info.row.original, "weight")}
              onCommit={(v) => commit(info.row.original, { weightKg: v === null ? null : Number(v) })}
            />
            <WasteReceiptCheckboxCell
              label="Estimate"
              checked={info.row.original.weightIsEstimate}
              disabled={!editable}
              onCommit={(checked) => commit(info.row.original, { weightIsEstimate: checked })}
            />
          </div>
        ),
      }),
      colHelper.accessor("hazardousWasteConsignmentCode", {
        header: "Consignment note",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            value={info.getValue()}
            upperCase
            placeholder="CJTILE/A0001"
            disabled={!editable}
            invalid={hasIssue(info.row.original, "consignment")}
            onCommit={(v) => commit(info.row.original, { hazardousWasteConsignmentCode: v })}
          />
        ),
      }),
      colHelper.accessor("carrierType", {
        header: "Carrier",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex flex-col gap-1">
              <WasteReceiptSelectCell
                value={row.carrierType}
                options={carrierTypeOptions}
                disabled={!editable}
                // Leaving third-party drops the haulier link; the API would anyway, this keeps the row honest.
                onCommit={(v) =>
                  commit(row, v === WasteReceiptCarrierType.ThirdPartyCarrier ? { carrierType: v } : { carrierType: v, haulierAccountId: null })
                }
              />
              {row.carrierType === WasteReceiptCarrierType.ThirdPartyCarrier && (
                <WasteReceiptHaulierCell
                  value={row.haulierAccountId}
                  options={haulierOptions(haulierList, row)}
                  disabled={!editable}
                  onPick={(haulierAccountId) => commit(row, { haulierAccountId, carrierType: WasteReceiptCarrierType.ThirdPartyCarrier })}
                  onCreate={() => {
                    setHaulierTargetRow(row);
                    setAddHaulierOpen(true);
                  }}
                />
              )}
            </div>
          );
        },
      }),
      colHelper.accessor("carrierRegistrationNumber", {
        header: "Carrier reg no.",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            value={info.getValue()}
            upperCase
            placeholder={info.row.original.carrierType === WasteReceiptCarrierType.HouseholdDropOff ? "n/a" : "CBDU123456"}
            // A linked haulier owns these two cells; edit the account instead.
            disabled={!editable || info.row.original.carrierType === WasteReceiptCarrierType.HouseholdDropOff || !!info.row.original.haulierAccountId}
            invalid={hasIssue(info.row.original, "carrier registration")}
            onCommit={(v) => commit(info.row.original, { carrierRegistrationNumber: v })}
          />
        ),
      }),
      colHelper.accessor("carrierOrganisationName", {
        header: "Carrier name",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            value={info.getValue()}
            width="w-44"
            disabled={!editable || !!info.row.original.haulierAccountId}
            invalid={hasIssue(info.row.original, "carrier name")}
            onCommit={(v) => commit(info.row.original, { carrierOrganisationName: v })}
          />
        ),
      }),
      colHelper.accessor("carrierVehicleRegistration", {
        header: "Carrier vehicle reg",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            value={info.getValue()}
            upperCase
            width="w-32"
            placeholder="Truck reg"
            disabled={!editable}
            invalid={hasIssue(info.row.original, "vehicle registration")}
            onCommit={(v) => commit(info.row.original, { carrierVehicleRegistration: v })}
          />
        ),
      }),
      colHelper.accessor("recoveryCode", {
        header: "Recovery code",
        enableSorting: false,
        cell: (info) => (
          <WasteReceiptTextCell
            value={info.getValue()}
            upperCase
            width="w-20"
            placeholder="R4"
            disabled={!editable}
            invalid={hasIssue(info.row.original, "recovery code")}
            onCommit={(v) => commit(info.row.original, { recoveryCode: v })}
          />
        ),
      }),
      colHelper.display({
        id: "detail",
        header: view === "submitted" ? "DEFRA response" : "Issues",
        cell: (info) => {
          const row = info.row.original;
          if (view === "submitted") {
            return (
              <div className="min-w-[12rem] text-xs text-gray-600">
                <p>{formatDateTime(row.submittedOn, DateTime.DATETIME_SHORT, "")}</p>
                {row.amendedOn && <p>Amended {formatDateTime(row.amendedOn, DateTime.DATETIME_SHORT, "")}</p>}
                {row.warnings?.length ? (
                  <ul className="list-disc pl-4 text-yellow-700">
                    {row.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          }

          return (
            <div className="min-w-[14rem] max-w-sm text-xs">
              {row.status === WasteReceiptStatus.Failed && row.failureReason && (
                <p className="mb-1 text-red-700">{row.failureReason}</p>
              )}
              {row.issues?.length ? (
                <ul className="list-disc pl-4 text-gray-700">
                  {row.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-green-700">Ready to submit</span>
              )}
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [view, editable, commit, haulierList]
  );

  const selectedFrom = (rows: RowModel<IWasteReceiptRow>) => rows.rows.map((r) => r.original.vehicleId);

  const runSequentially = async (ids: string[], action: (id: string) => Promise<unknown>, label: string) => {
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await action(id);
        ok++;
      } catch {
        failed++;
      }
    }
    addNotification({
      variant: failed === 0 ? "success" : "warn",
      primaryText: `${label}: ${ok} done${failed ? `, ${failed} failed` : ""}`,
    });
  };

  const actionButtons = (() => {
    if (view === "pending") {
      return [
        {
          label: "Submit selected",
          disabledOnNoRows: true,
          onClick: (rows: RowModel<IWasteReceiptRow>) => {
            setSelectedIds(selectedFrom(rows));
            setSubmitMode("selected");
            setSubmitOpen(true);
          },
        },
        {
          label: "Submit all ready",
          onClick: () => {
            setSubmitMode("ready");
            setSubmitOpen(true);
          },
        },
        {
          label: "Bulk update",
          disabledOnNoRows: true,
          onClick: (rows: RowModel<IWasteReceiptRow>) => {
            setSelectedIds(selectedFrom(rows));
            setBulkOpen(true);
          },
        },
        {
          label: "Exclude",
          disabledOnNoRows: true,
          classes: "bg-gray-600 hover:bg-gray-700 text-white",
          onClick: (rows: RowModel<IWasteReceiptRow>) => {
            exclude.mutate(selectedFrom(rows), {
              onSuccess: () => addNotification({ variant: "success", primaryText: "Vehicles excluded from waste tracking" }),
              onError: saveHandlers.onError,
            });
          },
        },
      ];
    }

    if (view === "excluded") {
      return [
        {
          label: "Restore",
          disabledOnNoRows: true,
          onClick: (rows: RowModel<IWasteReceiptRow>) => {
            restore.mutate(selectedFrom(rows), {
              onSuccess: () => addNotification({ variant: "success", primaryText: "Vehicles restored to pending" }),
              onError: saveHandlers.onError,
            });
          },
        },
      ];
    }

    return [
      {
        label: "Bulk update",
        disabledOnNoRows: true,
        onClick: (rows: RowModel<IWasteReceiptRow>) => {
          setSelectedIds(selectedFrom(rows));
          setBulkOpen(true);
        },
      },
      {
        label: "Resubmit amendment",
        disabledOnNoRows: true,
        onClick: (rows: RowModel<IWasteReceiptRow>) => {
          runSequentially(selectedFrom(rows), (id) => amend.mutateAsync(id), "Amendments");
        },
      },
    ];
  })();

  const resolveVehicleIds = async () => {
    if (submitMode === "selected") return selectedIds;
    const response = await getReadyIds(additionalBody.startDate ?? null, additionalBody.endDate ?? null);
    return response.data ?? [];
  };

  return (
    <>
      <WasteReceiptBulkUpdateModal open={bulkOpen} setOpen={setBulkOpen} vehicleIds={selectedIds} />
      <AccountCreateEditModal
        open={addHaulierOpen}
        setOpen={setAddHaulierOpen}
        presetHaulier
        onCreated={(account: IAccount) => {
          if (haulierTargetRow && account.isHaulier) {
            commit(haulierTargetRow, { haulierAccountId: account.id, carrierType: WasteReceiptCarrierType.ThirdPartyCarrier });
          }
        }}
      />
      <WasteReceiptSubmitModal
        open={submitOpen}
        setOpen={setSubmitOpen}
        resolveVehicleIds={resolveVehicleIds}
        title={submitMode === "selected" ? "Submit selected vehicles to DEFRA" : "Submit all ready vehicles to DEFRA"}
        description={
          submitMode === "selected"
            ? `${selectedIds.length} vehicle${selectedIds.length === 1 ? "" : "s"} selected.`
            : "Every pending vehicle in the current date range that passes the readiness checks."
        }
      />

      {siteSettingsIncomplete && view === "pending" && (
        <div className="mb-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
          Site settings are incomplete, so nothing can be submitted yet. Fill in the DEFRA settings under Admin &gt; Waste Tracking.
        </div>
      )}

      <PagedTable
        key={view}
        columns={cols}
        url={`${WASTE_TRACKING_TABLE_URL}/${view}`}
        pageMode={undefined}
        queryLabel="Search VRM or stock number"
        additionalBody={additionalBody}
        initialSort={[{ id: view === "submitted" ? "submittedOn" : "collectedOn", desc: true }]}
        onRowDoubleClick={(row: IWasteReceiptRow) => row.leadId && openInNewTab(`leads/${row.leadId}`)}
        showTotal
        setRowData={(rows: IWasteReceiptRow[]) =>
          setSiteSettingsIncomplete((rows ?? []).some((r) => r.issues?.some((i) => i.startsWith("Site settings incomplete"))))
        }
        actionButtons={actionButtons}
      />
    </>
  );
}
